import { useState, useEffect } from 'react'
import { networkClient } from '../network/client'

interface PuzzleData {
  puzzleId: string
  type: string
  difficulty: string
  sequence: string[]
}

interface PuzzlePanelProps {
  puzzle: PuzzleData
  onClose: () => void
}

export default function PuzzlePanel({ puzzle, onClose }: PuzzlePanelProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [inputValue, setInputValue] = useState('')
  const [feedback, setFeedback] = useState<string | null>(null)
  const [hint, setHint] = useState<string | null>(null)
  const [hintsUsed, setHintsUsed] = useState(0)
  const [solved, setSolved] = useState(false)
  const [solvedData, setSolvedData] = useState<{ points: number; message: string; correctIndex: number; correctValue: string; stabilityRestored?: number } | null>(null)

  useEffect(() => {
    const room = networkClient.getGameRoom()
    if (!room) return

    const handleWrong = (data: { message: string }) => {
      setFeedback(data.message)
      setTimeout(() => setFeedback(null), 3000)
    }

    const handleHint = (data: { hint: string; hintsUsed: number }) => {
      setHint(data.hint)
      setHintsUsed(data.hintsUsed)
    }

    const handleSolved = (data: { puzzleId: string; points: number; message: string; correctIndex: number; correctValue: string; stabilityRestored?: number }) => {
      if (data.puzzleId === puzzle.puzzleId) {
        setSolved(true)
        setSolvedData(data)
      }
    }

    room.onMessage('puzzle_wrong', handleWrong)
    room.onMessage('puzzle_hint', handleHint)
    room.onMessage('puzzle_solved', handleSolved)

    return () => {
      // Cleanup is handled by room leave
    }
  }, [puzzle.puzzleId])

  const handleSubmit = () => {
    if (selectedIndex === null || !inputValue.trim()) {
      setFeedback('Wybierz element i wprowadz poprawna wartosc')
      return
    }

    const room = networkClient.getGameRoom()
    if (room) {
      room.send('submit_puzzle_answer', {
        puzzleId: puzzle.puzzleId,
        selectedIndex,
        value: inputValue.trim(),
      })
    }
  }

  const requestHint = () => {
    const room = networkClient.getGameRoom()
    if (room) {
      room.send('request_hint', { puzzleId: puzzle.puzzleId })
    }
  }

  const getDifficultyColor = (diff: string) => {
    switch (diff) {
      case 'easy': return 'text-green-400'
      case 'medium': return 'text-yellow-400'
      case 'hard': return 'text-red-400'
      default: return 'text-white'
    }
  }

  const getDifficultyLabel = (diff: string) => {
    switch (diff) {
      case 'easy': return 'Latwa'
      case 'medium': return 'Srednia'
      case 'hard': return 'Trudna'
      default: return diff
    }
  }

  if (solved && solvedData) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
        <div className="bg-gray-800 border-2 border-emerald-neon rounded-xl p-8 max-w-lg text-center">
          <h2 className="text-3xl font-bold text-emerald-neon mb-4">Zagadka rozwiazana!</h2>
          <p className="text-xl text-white mb-4">{solvedData.message}</p>
          <div className="text-4xl font-bold text-yellow-400 mb-2">+{solvedData.points} pkt</div>
          {solvedData.stabilityRestored && (
            <div className="text-lg text-emerald-neon mb-4">+{solvedData.stabilityRestored}% stabilnosci</div>
          )}

          <div className="mb-6 p-4 bg-gray-900 rounded-lg">
            <p className="text-gray-400 mb-2">Poprawna odpowiedz:</p>
            <div className="flex justify-center gap-2 flex-wrap">
              {puzzle.sequence.map((item, idx) => (
                <div
                  key={idx}
                  className={`w-12 h-12 flex items-center justify-center rounded-lg font-bold text-lg
                    ${idx === solvedData.correctIndex
                      ? 'bg-emerald-neon text-gray-900'
                      : 'bg-gray-700 text-gray-300'
                    }`}
                >
                  {idx === solvedData.correctIndex ? solvedData.correctValue : item}
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={onClose}
            className="px-8 py-3 bg-emerald-neon text-gray-900 font-bold rounded-lg hover:bg-emerald-400"
          >
            Kontynuuj
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border-2 border-gray-600 rounded-xl p-6 max-w-2xl w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white">Zagadka: Rozpoznawanie wzorcow</h2>
            <p className={`text-sm ${getDifficultyColor(puzzle.difficulty)}`}>
              Trudnosc: {getDifficultyLabel(puzzle.difficulty)}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">&times;</button>
        </div>

        {/* Instructions */}
        <p className="text-gray-300 mb-6">
          Przeanalizuj sekwencje i znajdz element, ktory nie pasuje do wzorca.
          Kliknij na bledny element, a nastepnie wprowadz poprawna wartosc.
        </p>

        {/* Sequence */}
        <div className="flex justify-center gap-3 mb-6 flex-wrap">
          {puzzle.sequence.map((item, idx) => (
            <button
              key={idx}
              onClick={() => setSelectedIndex(idx)}
              className={`w-16 h-16 flex items-center justify-center rounded-lg font-bold text-xl transition-all
                ${selectedIndex === idx
                  ? 'bg-yellow-500 text-gray-900 ring-4 ring-yellow-300'
                  : 'bg-gray-700 text-white hover:bg-gray-600'
                }
                ${solved && solvedData?.correctIndex === idx ? 'bg-emerald-neon text-gray-900' : ''}
              `}
            >
              {item}
            </button>
          ))}
        </div>

        {/* Index labels */}
        <div className="flex justify-center gap-3 mb-6">
          {puzzle.sequence.map((_, idx) => (
            <div key={idx} className="w-16 text-center text-xs text-gray-500">
              [{idx}]
            </div>
          ))}
        </div>

        {/* Input */}
        {selectedIndex !== null && (
          <div className="mb-6">
            <label className="block text-sm text-gray-400 mb-2">
              Poprawna wartosc dla pozycji [{selectedIndex}]:
            </label>
            <div className="flex gap-3">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Wprowadz poprawna wartosc..."
                className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white
                           focus:border-emerald-neon focus:outline-none text-lg"
                maxLength={10}
              />
              <button
                onClick={handleSubmit}
                className="px-6 py-3 bg-emerald-neon text-gray-900 font-bold rounded-lg hover:bg-emerald-400"
              >
                Sprawdz
              </button>
            </div>
          </div>
        )}

        {/* Feedback */}
        {feedback && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-500 text-red-300 rounded-lg">
            {feedback}
          </div>
        )}

        {/* Hint */}
        {hint && (
          <div className="mb-4 p-3 bg-blue-900/50 border border-blue-500 text-blue-300 rounded-lg">
            <strong>Podpowiedz:</strong> {hint}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center">
          <button
            onClick={requestHint}
            disabled={hintsUsed >= 3}
            className="px-4 py-2 text-gray-400 hover:text-yellow-400 disabled:opacity-50"
          >
            Popros o podpowiedz ({hintsUsed}/3)
            {hintsUsed > 0 && <span className="text-xs ml-1">(-{hintsUsed === 1 ? 0 : hintsUsed === 2 ? 50 : 150} pkt)</span>}
          </button>

          <div className="text-gray-500 text-sm">
            Nagroda: {puzzle.difficulty === 'easy' ? 150 : puzzle.difficulty === 'medium' ? 250 : 500} pkt
          </div>
        </div>
      </div>
    </div>
  )
}
